import { Button, type DialogProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCreateZone } from '~/api/zones';
import { Dialog, DialogContentTitle, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';
import { useLayout } from '../../context';
import { useZoneFormik } from '../lib';
import { ZoneForm, type ZoneFormValues } from './zone-form';

export interface NewZoneDialogProps extends Omit<DialogProps, 'children'> {
  close: () => void;
  open: boolean;
}

export const NewZoneDialog = ({ close, open }: NewZoneDialogProps) => {
  const { t } = useTranslation();
  const layout = useLayout();
  const [createZone] = useCreateZone();

  const onSubmit = async (values: ZoneFormValues) => {
    const { errors } = await createZone({
      refetchQueries: ['LayoutGet'],
      variables: { patch: values, layoutId: layout.id },
    });
    if (!errors?.length) close();
  };

  const formik = useZoneFormik(
    {
      height: 1080,
      left: 0,
      name: '',
      top: 0,
      width: 1920,
    },
    onSubmit,
  );

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={close}
      TransitionProps={{ onExited: () => formik.resetForm() }}
    >
      <DialogTitle onClose={close}>{t('pages.layouts.detail.new_zone_dialog.title')}</DialogTitle>

      <DialogContent>
        <DialogContentTitle>
          {t('pages.layouts.detail.new_zone_dialog.description')}
        </DialogContentTitle>
        <ZoneForm formik={formik} id="new-zone-form" />
      </DialogContent>

      <DialogActions>
        <Button disabled={formik.isSubmitting} onClick={close} variant="outlined">
          {t('common.actions.cancel')}
        </Button>
        <Button
          color="primary"
          form="new-zone-form"
          disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
          type="submit"
          variant="contained"
        >
          {t('common.dialogs.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
